/**
 * Which modules this business has.
 *
 * CenoX is one product with several verticals in it. A retail shop has no
 * riders and no drop-off addresses; a logistics business has no shelves to
 * count. Both share the same books underneath — a sale is a sale — but the
 * operational screens are not interchangeable, and showing a shop a Deliveries
 * menu it can never use is worse than showing it nothing.
 *
 * So navigation asks two questions, not one: may this person do it, and does
 * this business have it at all.
 */

import { useQuery } from '@tanstack/react-query';
import type { ModuleKey } from '@cenox/shared';
import { api } from './api';
import { useAuth, useBusinessPath } from './auth';

interface ModuleRow {
  key: ModuleKey;
  label: string;
  enabled: boolean;
  granted: boolean;
  core: boolean;
}

export function useModules(): { active: Set<ModuleKey>; isLoading: boolean; known: boolean } {
  const { business } = useAuth();
  const path = useBusinessPath();

  const query = useQuery({
    queryKey: ['modules', business?.businessId],
    queryFn: () => api.get<{ data: ModuleRow[] }>(path('/modules')),
    enabled: Boolean(business),
    // Modules change when somebody edits a plan or a switch, which is rare and
    // never mid-task. Re-asking on every screen would be noise.
    staleTime: 5 * 60 * 1000,
  });

  const active = new Set<ModuleKey>(
    (query.data?.data ?? []).filter((m) => m.enabled).map((m) => m.key),
  );

  /**
   * Whether the answer is actually known.
   *
   * A worker has no `settings:read`, so this call refuses for them — and an
   * empty set would then hide the one tool they exist to use. Modules are a
   * tidying-up of the menu; PERMISSIONS are the gate, and the API enforces them
   * regardless. So when we cannot tell, we do not filter.
   */
  return { active, isLoading: query.isPending, known: !query.isError };
}

/**
 * Whether one module is on.
 *
 * Returns false while loading, deliberately. A menu item that appears a beat
 * after everything else draws the eye to exactly the wrong place, and a screen
 * that renders before its entitlement is known is a screen that flashes data it
 * may not be allowed to show.
 */
export function useModule(key: ModuleKey): boolean {
  const { active, known } = useModules();
  return known ? active.has(key) : true;
}
